import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './LanguageSwitcher.css';

// Drapeaux inline SVG — aucune dépendance externe, fonctionne partout
const FLAG_FR = `data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 3 2'><rect width='1' height='2' fill='%23002395'/><rect x='1' width='1' height='2' fill='%23fff'/><rect x='2' width='1' height='2' fill='%23ED2939'/></svg>`;
const FLAG_EN = `data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 60 30'><rect width='60' height='30' fill='%23012169'/><path d='M0,0 L60,30 M60,0 L0,30' stroke='%23fff' stroke-width='6'/><path d='M0,0 L60,30 M60,0 L0,30' stroke='%23C8102E' stroke-width='4'/><path d='M30,0 V30 M0,15 H60' stroke='%23fff' stroke-width='10'/><path d='M30,0 V30 M0,15 H60' stroke='%23C8102E' stroke-width='6'/></svg>`;

const LANGUAGES = [
  { code: 'fr', label: 'Français', flag: FLAG_FR, short: 'FR' },
  { code: 'en', label: 'English',  flag: FLAG_EN, short: 'EN' },
];

const LanguageSwitcher = ({ variant = 'dropdown' }) => {
  const { i18n, t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const current = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  const changeLanguage = useCallback((code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  }, [i18n]);

  // Fermer au clic extérieur
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Fermer à Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Variant "pills" : affiche les deux langues côte à côte
  if (variant === 'pills') {
    return (
      <div className="lang-pills" role="group" aria-label={t('langSwitcher.group', 'Language selection')}>
        {LANGUAGES.map(lang => (
          <button
            key={lang.code}
            className={`lang-pill${i18n.language === lang.code ? ' active' : ''}`}
            onClick={() => changeLanguage(lang.code)}
            aria-pressed={i18n.language === lang.code}
            title={lang.label}
          >
            <img className="lang-flag" src={lang.flag} alt={lang.label} aria-hidden="true" />
            <span className="lang-short">{lang.short}</span>
          </button>
        ))}
      </div>
    );
  }

  // Variant par défaut : dropdown élégant
  return (
    <div className="lang-switcher" ref={ref}>
      <button
        className={`lang-trigger${open ? ' open' : ''}`}
        onClick={() => setOpen(v => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('langSwitcher.current', 'Language: {{lang}}', { lang: current.label })}
      >
        <img className="lang-flag" src={current.flag} alt={current.label} aria-hidden="true" />
        <span className="lang-label">{current.short}</span>
        <span className={`lang-chevron${open ? ' up' : ''}`} aria-hidden="true">▾</span>
      </button>

      {open && (
        <ul
          className="lang-dropdown"
          role="listbox"
          aria-label={t('langSwitcher.listbox', 'Choose language')}
        >
          {LANGUAGES.map(lang => (
            <li
              key={lang.code}
              role="option"
              aria-selected={i18n.language === lang.code}
              className={`lang-option${i18n.language === lang.code ? ' selected' : ''}`}
              onClick={() => changeLanguage(lang.code)}
              onKeyDown={e => e.key === 'Enter' && changeLanguage(lang.code)}
              tabIndex={0}
            >
              <img className="lang-flag" src={lang.flag} alt={lang.label} aria-hidden="true" />
              <span className="lang-name">{lang.label}</span>
              {i18n.language === lang.code && (
                <span className="lang-check" aria-hidden="true">✓</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LanguageSwitcher;
